<template>
  <div class="export-content__settings">
    <div class="export-content__setting export-content__notion">
      <p class="export-content__hint export-content__step-hint">
        <span class="export-content__step-info">Step 1</span>: Create a new
        <a
          class="export-content__link"
          target="_blank"
          href="https://www.notion.so/my-integrations"
          >Notion integration</a
        >, then copy and paste the Internal Integration Token below.
      </p>
      <form
        role="form"
        class="form export-content__form_notion"
        novalidate
        @submit.prevent="checkForm"
      >
        <div class="export-content__form--section">
          <div class="export-content__label-wrapper">
            <label class="export-content__label" for="token">API key</label>
            <FormError :errors="validation.errors" field="notion_token" />
          </div>
          <InputWithContextMenu
            id="notion_integration_token"
            ref="input_token"
            v-model="token"
            name="token"
            rows="1"
            type="text"
            class="export-content__input"
            placeholder="Paste Notion API key"
            :disabled="isNotionEnabled"
            :class="validation.stateClass('notion_token')"
          />
        </div>

        <p
          class="export-content__hint export-content__step-hint"
          :class="{ 'export-content__step-disabled': !token }"
        >
          <span class="export-content__step-info">Step 2</span>: Open our
          pre-configured
          <a
            class="export-content__link"
            target="_blank"
            href="https://shortform.notion.site/shortform/fcfff9e5e04e47929f2dbb925ecde80d"
            >Shortform database template</a
          >
          and click on <strong>"Duplicate"</strong> on the upper right corner to
          add it to your Notion workspace.
        </p>

        <p
          class="export-content__hint export-content__step-hint"
          :class="{ 'export-content__step-disabled': !token }"
        >
          <span class="export-content__step-info">Step 3</span>: On the newly
          created database, click the <strong>three-dot icon (...)</strong> on
          the upper right, and click on <strong>"Add connections"</strong>. Find
          the name of the integration you created, click it, then press
          <strong>"Confirm"</strong>.
        </p>

        <p
          class="export-content__hint export-content__step-hint"
          :class="{ 'export-content__step-disabled': !token }"
        >
          <span class="export-content__step-info">Step 4</span>: Copy and paste
          your new database's URL below!
        </p>

        <div
          class="export-content__form--section"
          :class="{ 'export-content__step-disabled': !token }"
        >
          <div class="export-content__label-wrapper">
            <label class="export-content__label" for="database_url"
              >Notion link</label
            >
            <FormError :errors="validation.errors" field="notion_token" />
          </div>

          <InputWithContextMenu
            id="notion_database_id"
            ref="input_url"
            v-model="database"
            name="database"
            rows="1"
            type="text"
            class="export-content__input"
            placeholder="https://"
            :disabled="isNotionEnabled || !token"
            :class="validation.stateClass('notion_token')"
          />
        </div>

        <div
          class="export-content__form--section"
          :class="{ 'export-content__step-disabled': !token || !database }"
        >
          <div
            class="export-content__wrapper-info export-content__wrapper export-content__step-hint"
          >
            <span class="export-content__wrapper--text">
              This integration automatically exports your highlights to the
              selected Notion database. If you have used the integration before
              and you would like to avoid duplicates, you may want to create a
              new database.
            </span>
          </div>
          <div class="export-content__submit-buttons">
            <button
              v-if="!isNotionEnabled"
              v-wait:disabled="'Enabling'"
              type="submit"
              :disabled="!token || !database"
              class="btns"
            >
              <v-wait for="Enabling">
                <template v-slot:waiting>
                  Enabling
                  <button-spinner />
                </template>
                Connect and Export
              </v-wait>
            </button>
            <button
              v-if="isNotionEnabled"
              v-wait:disabled="'Disconnecting'"
              type="submit"
              class="btns export-content__button-cancel"
              :disabled="!token || !database"
            >
              <v-wait for="Disconnecting">
                <template v-slot:waiting>
                  Disconnecting
                  <button-spinner />
                </template>
                Disconnect
              </v-wait>
            </button>
          </div>
        </div>
        <DisablePopup
          ref="popupDisable"
          integration-name="Notion"
          :disableIntegration="disableIntegration"
          :afterDisableCallback="refresh"
          @updateEnabled="$emit('updateEnabled', false)"
        />
      </form>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onBeforeMount, ref } from 'vue'
import { useWait } from '@/app/composables/useWait'
import { Sentry } from '@/services/sentry'

import ButtonSpinner from '@/components/ui/ButtonSpinner.vue'
import FormError from '@/components/ui/FormError.vue'
import InputWithContextMenu from '@/app/components/InputWithContextMenu.vue'
import { ValidationState } from '@/helpers/form'

import DisablePopup from '@/app/components/export/DisablePopup.vue'
import { backend } from '@/services/backend'
import { parseDatabaseUrl } from '@/services/notion'

const validation = ref(new ValidationState())
const token = ref<string | null>(null)
const database = ref<string | null>(null)

const props = withDefaults(
  defineProps<{
    isNotionEnabled?: boolean
  }>(),
  {
    isNotionEnabled: false,
  },
)

const emit = defineEmits<{
  (e: 'updateEnabled', value: boolean): void
}>()
const wait = useWait()

const popupDisable = ref<InstanceType<typeof DisablePopup> | null>(null)

onBeforeMount(async () => {
  await refresh()
  validation.value.reset()
})

async function refresh(): Promise<void> {
  const credentials = await backend.getNotionCredentials()
  token.value = credentials.notion_integration_token
  if (!credentials.notion_database_id) {
    database.value = null
  } else {
    database.value = `https://www.notion.so/${credentials.notion_database_id}`
  }
  emit('updateEnabled', token.value !== null && database.value !== null)
}

async function checkForm(): Promise<void> {
  if (props.isNotionEnabled) {
    disableNotion()
  } else {
    await enableNotion()
  }
}

function getErrorMessage(error: any): string {
  if (error.name === 'TypeError' && error.toString().includes('URL')) {
    return 'Invalid Notion Link'
  } else if (error.response === undefined) {
    Sentry.captureException(error)
    return 'Could not talk to backend. Network or authorisation error?'
  } else {
    const maybeErrorMessage = error?.response?.data?.errors?._schema?.[0]
    if (maybeErrorMessage === 'Insufficient permissions for this endpoint.') {
      return 'Make sure your integration has read and insert capabilities.'
    }
    Sentry.captureException(error)
    return (
      maybeErrorMessage ?? 'Notion integration error. Please contact support.'
    )
  }
}

async function enableNotion(): Promise<void> {
  try {
    validation.value.reset()
    wait.start('Enabling')
    const credentials = {
      notion_database_id: parseDatabaseUrl(database.value || ''),
      notion_integration_token: token.value || '',
    }
    await backend.saveNotionCredentials(credentials)
    emit('updateEnabled', true)
  } catch (error) {
    validation.value.showMessage('notion_token', getErrorMessage(error))
  } finally {
    wait.end('Enabling')
  }
}

function disableNotion(): void {
  popupDisable.value?.show()
}

async function disableIntegration(): Promise<void> {
  await backend.removeNotionCredentials()
}
</script>
